import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    LineChart as LineIcon,
    ArrowLeft,
    Loader2,
    Calculator as CalcIcon,
    Download,
    TrendingUp,
    TrendingDown,
    Target,
    Share2,
} from "lucide-react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    ReferenceLine,
} from "recharts";
import CountUp from "react-countup";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import useSeo from "../../hooks/useSeo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import ShareDialog from "../../components/calc/ShareDialog";

const initialForm = {
    total_investment: 380000000,
    room_count: 120,
    avg_nightly_rate: 3500,
    occupancy_rate: 0.65,
    monthly_operating_cost: null,
};

export default function RoiCalculator() {
    const [form, setForm] = useState(initialForm);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [shareModal, setShareModal] = useState(false);

    useSeo({
        title: "ROI Hesaplayıcı",
        description: "Otel yatırımı geri dönüş süresi ve net kâr analizi — iyimser/normal/kötümser senaryolar.",
        path: "/araclar/roi-hesaplayici",
    });

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const calculate = async () => {
        setLoading(true);
        try {
            const payload = { ...form };
            if (!payload.monthly_operating_cost) delete payload.monthly_operating_cost;
            const { data } = await api.post("/calculator/roi", payload);
            setResult(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const downloadPdf = async () => {
        try {
            const payload = { ...form };
            if (!payload.monthly_operating_cost) delete payload.monthly_operating_cost;
            const res = await api.post("/calculator/roi/pdf", payload, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "otelpro-roi-raporu.pdf";
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success("PDF indirildi");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="bg-light-bg min-h-[80vh]" data-testid="roi-calculator-page">
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-5xl mx-auto px-6 py-12 lg:py-16">
                    <Link to="/araclar" className="text-xs uppercase tracking-[0.2em] text-teal font-semibold inline-flex items-center gap-1 hover:text-white">
                        <ArrowLeft className="w-3.5 h-3.5" /> Tüm araçlar
                    </Link>
                    <h1 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight inline-flex items-center gap-3">
                        <LineIcon className="w-8 h-8 text-gold" />
                        ROI Hesaplayıcı
                    </h1>
                    <p className="mt-3 text-white/75 max-w-2xl">
                        Yatırımın yıllık net kârını ve geri dönüş süresini iyimser, normal ve kötümser senaryolarla görün.
                    </p>
                </div>
            </section>

            <div className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-2 gap-6">
                {/* FORM */}
                <Card title="Girdiler">
                    <div className="space-y-4">
                        <NumberField label="Toplam Yatırım (₺)" value={form.total_investment} onChange={(v) => update("total_investment", v)} testId="roi-total-investment" />
                        <NumberField label="Oda Sayısı" value={form.room_count} onChange={(v) => update("room_count", v)} step={1} testId="roi-rooms" />
                        <NumberField label="Ortalama Oda Fiyatı / ADR (₺)" value={form.avg_nightly_rate} onChange={(v) => update("avg_nightly_rate", v)} testId="roi-adr" />
                        <div>
                            <span className="text-xs font-semibold text-deep-blue/80 uppercase tracking-wider">
                                Doluluk Oranı: <span className="font-data text-dark-navy">{Math.round(form.occupancy_rate * 100)}%</span>
                            </span>
                            <input
                                type="range"
                                min={20}
                                max={95}
                                value={Math.round(form.occupancy_rate * 100)}
                                onChange={(e) => update("occupancy_rate", parseInt(e.target.value) / 100)}
                                className="w-full mt-2 accent-teal"
                                data-testid="roi-occupancy"
                            />
                        </div>
                        <NumberField
                            label="Aylık İşletme Gideri (opsiyonel, ₺)"
                            value={form.monthly_operating_cost || ""}
                            onChange={(v) => update("monthly_operating_cost", v || null)}
                            testId="roi-monthly-opex"
                        />
                        <button
                            onClick={calculate}
                            disabled={loading}
                            className="w-full bg-gold hover:bg-gold-700 text-dark-navy font-bold text-sm py-3 rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-70 shadow-md"
                            data-testid="roi-calculate-btn"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalcIcon className="w-4 h-4" />}
                            Hesapla
                        </button>
                    </div>
                </Card>

                {/* RESULT */}
                {result ? (
                    <Card title="Sonuç">
                        <div className="bg-dark-navy text-white rounded-lg p-5 -mx-2 -mt-2">
                            <span className="text-[10px] uppercase tracking-[0.2em] text-teal font-semibold">Geri Dönüş</span>
                            <div className="mt-1 font-data text-4xl sm:text-5xl font-bold text-gold">
                                {result.payback_period_years ? (
                                    <><CountUp end={result.payback_period_years} decimals={1} duration={1.4} /> <span className="text-xl text-white/80">yıl</span></>
                                ) : "—"}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-4">
                            <ResultStat label="Yıllık Gelir" value={result.annual_revenue} accent="teal" />
                            <ResultStat label="Yıllık Net Kâr" value={result.annual_net_profit} accent="gold" />
                            <ResultStat label="Yıllık Gider" value={result.annual_opex} negative />
                            <ResultStat label="Aylık Net Kâr" value={result.annual_net_profit / 12} accent="teal" />
                        </div>
                        <button
                            onClick={downloadPdf}
                            className="mt-4 w-full bg-deep-blue hover:bg-dark-navy text-white text-sm font-semibold py-2.5 rounded-md inline-flex items-center justify-center gap-2"
                            data-testid="roi-pdf-btn"
                        >
                            <Download className="w-4 h-4" /> PDF Rapor İndir
                        </button>
                        <button
                            onClick={() => setShareModal(true)}
                            className="mt-2 w-full bg-teal hover:bg-teal-700 text-dark-navy text-sm font-bold py-2.5 rounded-md inline-flex items-center justify-center gap-2"
                            data-testid="roi-share-btn"
                        >
                            <Share2 className="w-4 h-4" /> Sosyal Medyada Paylaş
                        </button>
                    </Card>
                ) : (
                    <Card title="Sonuç">
                        <div className="text-center py-12 text-deep-blue/60 text-sm">
                            <Target className="w-10 h-10 mx-auto mb-3 text-teal/50" />
                            Hesaplamak için soldaki formu doldurup "Hesapla" butonuna basın.
                        </div>
                    </Card>
                )}
            </div>

            {result && (
                <div className="max-w-5xl mx-auto px-6 pb-14 space-y-6">
                    {/* Break-even chart */}
                    <Card title="Break-Even Analizi (60 ay)">
                        <div className="h-72" data-testid="roi-breakeven-chart">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={result.break_even_data}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#64748b" label={{ value: "Ay", position: "insideBottom", offset: -3, fontSize: 10 }} />
                                    <YAxis tick={{ fontSize: 11 }} stroke="#64748b" tickFormatter={(v) => `${(v / 1_000_000).toFixed(0)}M`} />
                                    <Tooltip
                                        contentStyle={{ borderRadius: 8, fontSize: 12 }}
                                        formatter={(v) => `${(v / 1_000_000).toFixed(2)}M ₺`}
                                    />
                                    <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="4 4" label={{ value: "Başabaş", fontSize: 10, fill: "#ef4444" }} />
                                    <Line
                                        type="monotone"
                                        dataKey="cumulative_profit"
                                        stroke="#39c8c1"
                                        strokeWidth={3}
                                        dot={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>

                    {/* Scenarios */}
                    <Card title="Senaryolar">
                        <Tabs defaultValue="normal">
                            <TabsList className="bg-light-bg">
                                <TabsTrigger value="optimistic" className="data-[state=active]:bg-data-green data-[state=active]:text-white">İyimser (+20%)</TabsTrigger>
                                <TabsTrigger value="normal" className="data-[state=active]:bg-dark-navy data-[state=active]:text-white">Normal</TabsTrigger>
                                <TabsTrigger value="pessimistic" className="data-[state=active]:bg-data-red data-[state=active]:text-white">Kötümser (-20%)</TabsTrigger>
                            </TabsList>
                            {Object.entries(result.scenarios).map(([name, s]) => (
                                <TabsContent key={name} value={name} className="pt-4">
                                    <div className="grid sm:grid-cols-3 gap-3">
                                        <ResultStat label="Yıllık Gelir" value={s.annual_revenue} />
                                        <ResultStat label="Net Kâr" value={s.annual_net_profit} accent="gold" />
                                        <ResultStat
                                            label="Geri Dönüş"
                                            value={s.payback_months ? s.payback_months / 12 : 0}
                                            suffix=" yıl"
                                            decimals={1}
                                            accent="teal"
                                        />
                                    </div>
                                </TabsContent>
                            ))}
                        </Tabs>
                    </Card>
                </div>
            )}

            {shareModal && result && (
                <ShareDialog
                    open={shareModal}
                    onClose={() => setShareModal(false)}
                    tool="roi"
                    summary={{
                        total_investment: form.total_investment,
                        annual_net_profit: result.annual_net_profit,
                        annual_revenue: result.annual_revenue,
                        payback_period_years: result.payback_period_years,
                        room_count: form.room_count,
                        occupancy_rate: form.occupancy_rate,
                    }}
                />
            )}
        </div>
    );
}

function Card({ title, children }) {
    return (
        <div className="bg-white rounded-lg border border-slate-100 shadow-md p-6">
            {title && <h2 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-4">{title}</h2>}
            {children}
        </div>
    );
}

function NumberField({ label, value, onChange, step = 1000, testId }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-deep-blue/80 uppercase tracking-wider">{label}</span>
            <input
                type="number"
                step={step}
                value={value || ""}
                onChange={(e) => onChange(e.target.value === "" ? "" : parseFloat(e.target.value))}
                className="mt-1 w-full px-3 py-2.5 rounded-md border border-slate-300 text-sm font-data outline-none focus:border-teal"
                data-testid={testId}
            />
        </label>
    );
}

function ResultStat({ label, value, suffix = " ₺", decimals = 0, accent = "deep-blue", negative }) {
    const color = negative ? "text-data-red" : accent === "gold" ? "text-gold-700" : accent === "teal" ? "text-teal-700" : "text-dark-navy";
    return (
        <div className="rounded-md bg-light-bg p-3">
            <div className="text-[10px] uppercase tracking-wider text-deep-blue/70 font-semibold">{label}</div>
            <div className={`mt-1 font-data text-xl font-bold ${color}`}>
                {negative ? "- " : ""}
                <CountUp end={Math.abs(value || 0)} decimals={decimals} duration={1.2} separator="." />{suffix}
            </div>
        </div>
    );
}
